/**
 * Modelo base de todos os registros do Family Life OS.
 *
 * Regra de arquitetura (ADR-0004): NENHUMA escrita e destrutiva.
 * Apagar significa marcar `deletedAt`. Editar significa incrementar `version`
 * e registrar uma entrada de auditoria com o valor anterior.
 */

/** Estado de sincronizacao de um registro local face ao servidor. */
export type SyncState =
  /** Criado offline, ainda nao entrou na fila de envio. */
  | 'local'
  /** Na fila de envio (outbox), a aguardar ligacao. */
  | 'pending'
  /** Confirmado pelo servidor. */
  | 'synced'
  /** O servidor tem uma versao divergente; precisa de revisao humana. */
  | 'conflict';

/** Campos presentes em todas as entidades de negocio. */
export interface BaseRecord {
  /** ULID gerado no cliente — ordenavel por tempo, seguro offline. */
  id: string;
  /** Todos os dados pertencem a uma familia. Fronteira de isolamento. */
  familyId: string;
  /** ISO-8601 UTC. */
  createdAt: string;
  /** id do membro que criou. */
  createdBy: string;
  /** ISO-8601 UTC. */
  updatedAt: string;
  /** id do membro que alterou pela ultima vez. */
  updatedBy: string;
  /** Soft delete. `null`/ausente = ativo. */
  deletedAt?: string | null;
  /** id de quem apagou. */
  deletedBy?: string | null;
  /** Contador otimista. Incrementa a cada escrita. Deteta conflitos. */
  version: number;
  /** Estado face ao servidor. Nunca sai do dispositivo. */
  syncState: SyncState;
}

/** Campos que o chamador fornece; o repositorio preenche o resto. */
export type NewRecord<T extends BaseRecord> = Omit<
  T,
  keyof BaseRecord | 'entity'
> & { id?: string };

/** Alteracao parcial a um registro existente. */
export type RecordPatch<T extends BaseRecord> = Partial<
  Omit<T, keyof BaseRecord | 'entity'>
>;

/** Operacoes auditaveis. */
export type AuditOperation = 'create' | 'update' | 'delete' | 'restore';

/** Origem de uma escrita — essencial para distinguir acoes da IA das humanas. */
export type AuditActor = 'user' | 'ai' | 'automation' | 'sync' | 'system';

/**
 * Entrada imutavel do registro de auditoria.
 * Guarda o suficiente para reverter a operacao (ADR-0004).
 */
export interface AuditEntry {
  id: string;
  familyId: string;
  /** Nome da entidade, ex. 'finance.transaction'. */
  entity: string;
  /** id do registro afetado. */
  recordId: string;
  operation: AuditOperation;
  actor: AuditActor;
  /** id do membro responsavel (mesmo quando actor='ai', quem confirmou). */
  memberId: string;
  at: string;
  /** Versao do registro ANTES da operacao. `null` num create. */
  before: unknown | null;
  /** Versao do registro DEPOIS da operacao. `null` num delete definitivo. */
  after: unknown | null;
  /** Se veio da IA: que intencao a originou. Permite auditar a IA. */
  aiTraceId?: string;
  /** Se veio de uma automacao: que regra disparou. */
  automationRuleId?: string;
  /**
   * Se esta operacao foi a anulacao de outra: o id da entrada anulada.
   * Sem isto, uma anulacao e indistinguivel de uma edicao manual — e a diferenca
   * importa a quem esta a tentar perceber o que aconteceu aos seus dados.
   */
  revertedFrom?: string;
  /** Justificacao opcional, em portugues. Mostrada no historico. */
  reason?: string;
}

/** Descreve uma entidade que um plugin possui. Usado pelo motor de sync e pela IA. */
export interface EntityDescriptor {
  /** Identificador global, ex. 'finance.transaction'. Prefixo = id do plugin. */
  name: string;
  /** Nome legivel em portugues, ex. 'Despesa'. Usado nas respostas da IA. */
  label: string;
  /** Plural legivel, ex. 'Despesas'. */
  labelPlural: string;
  /** Folha correspondente no Google Sheets (backend MVP). */
  sheet: string;
  /** Campos indexados no IndexedDB, alem dos campos base. */
  indexes: string[];
  /** Se true, a IA pode ler estes registros para responder a perguntas. */
  aiReadable: boolean;
  /** Se true, a IA pode propor escritas (sempre com confirmacao). */
  aiWritable: boolean;
  /** Nivel de sensibilidade — controla o que sai do dispositivo (ADR-0006). */
  sensitivity: 'low' | 'medium' | 'high';
}

export function isDeleted(record: BaseRecord): boolean {
  return record.deletedAt != null;
}

export function isActive(record: BaseRecord): boolean {
  return record.deletedAt == null;
}
